// src/components/cliente/SelectorFechas.jsx

import React, { useContext, useEffect, useState, useRef } from 'react';
import { CitaContext } from '../../context/CitaContext';

const cx = (...classes) => classes.filter(Boolean).join(' ');

const TarjetaFecha = ({ item, seleccionada, onSelect }) => (
  <div
    onClick={() => onSelect(item.fecha)}
    className={cx('booking-date-card', seleccionada && 'is-selected')}
  >
    <div className="booking-date-weekday">
      {item.fecha.toLocaleDateString('es-ES', { weekday: 'short' }).toUpperCase()}
    </div>
    <div className="booking-date-day-container">
      <div className="booking-date-day">{item.fecha.getDate()}</div>
      <div className="booking-date-month">
        {item.fecha.toLocaleDateString('es-ES', { month: 'short' })}
      </div>
    </div>
  </div>
);

const TarjetaHorario = ({ horario, seleccionado, onSelect }) => {
  const disponible = horario.disponible;

  return (
    <div
      onClick={() => disponible && onSelect(horario.hora)}
      className={cx(
        'booking-time-card',
        seleccionado && 'is-selected',
        !disponible && 'is-unavailable'
      )}
    >
      <div className="booking-time-value">{horario.hora}</div>
      {!disponible && (
        <div className="booking-time-status unavailable">
          <i className="fas fa-times-circle"></i> Ocupado
        </div>
      )}
      {seleccionado && (
        <div className="booking-time-status selected">
          <i className="fas fa-check-circle"></i> Seleccionado
        </div>
      )}
    </div>
  );
};

const SelectorFechas = () => {
  const { 
    servicioSeleccionado,
    fechasDisponibles, 
    fechasVerificadas,
    fechaSeleccionada, 
    setFechaSeleccionada,
    horaSeleccionada,
    setHoraSeleccionada,
    cargarDisponibilidadFecha,
    cargandoFechas,
    errorFechas
  } = useContext(CitaContext);
  
  const [cargandoInicial, setCargandoInicial] = useState(true);
  const verificacionIniciada = useRef(false);

  // Tomamos el mes de la primera fecha para filtrar (0 = enero, …, 3 = abril)
  const mesServicio = fechasVerificadas[0]?.fecha.getMonth();

  // Verificar disponibilidad de todas las fechas
  useEffect(() => {
    const verificarTodasLasFechas = async () => {
      if (!servicioSeleccionado?.doctorId || verificacionIniciada.current) return;
      verificacionIniciada.current = true;
      setCargandoInicial(true);
      try {
        for (const item of fechasDisponibles) {
          if (!item.cargado) {
            await cargarDisponibilidadFecha(
              servicioSeleccionado.doctorId, 
              item.fecha
            );
            await new Promise(r => setTimeout(r, 100));
          }
        }
      } catch (error) {
        console.error('Error al verificar fechas:', error);
      } finally {
        setCargandoInicial(false);
      }
    };
    verificarTodasLasFechas();
  }, [servicioSeleccionado, fechasDisponibles, cargarDisponibilidadFecha]);

  // Seleccionar la primera fecha verificada
  useEffect(() => {
    if (!cargandoInicial && fechasVerificadas.length > 0 && !fechaSeleccionada) {
      setFechaSeleccionada(fechasVerificadas[0].fecha);
    }
  }, [cargandoInicial, fechasVerificadas, fechaSeleccionada, setFechaSeleccionada]);

  const handleSeleccionarFecha = (fecha) => {
    setFechaSeleccionada(fecha);
    setHoraSeleccionada(null);
  };

  const handleSeleccionarHora = (hora) => setHoraSeleccionada(hora);

  const horariosSeleccionados =
    fechasVerificadas.find((item) => item.fecha.getTime() === fechaSeleccionada?.getTime())?.horarios || [];

  return (
    <div style={{ width: '100%' }}>
      <p className="booking-info">
        <i className="fas fa-calendar-check"></i>
        Seleccione la fecha y hora que mejor se adapte a su disponibilidad.
      </p>

      <div style={{ marginBottom: '30px' }}>
        <h4 className="booking-section-title">
          <i className="fas fa-calendar-alt"></i>
          Fechas disponibles
        </h4>

        {errorFechas && (
          <div className="booking-alert-error">
            <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
            {errorFechas}
          </div>
        )}

        {cargandoInicial && (
          <div className="booking-state-box loading">
            <i className="fas fa-spinner fa-spin" style={{ marginRight: '10px' }}></i>
            Verificando fechas disponibles...
          </div>
        )}

        {!cargandoInicial && fechasVerificadas.length === 0 && (
          <div className="booking-state-box empty">
            <i className="fas fa-calendar-times" style={{ fontSize: '24px', color: '#999', marginBottom: '10px' }}></i>
            <p>No hay fechas disponibles en los próximos días.</p>
            <p style={{ fontSize: '14px' }}>Por favor, contacte directamente con la clínica.</p>
          </div>
        )}

        {!cargandoInicial && fechasVerificadas.length > 0 && (
          <>
            <div className="booking-scroll-hint-wrap">
              <div className="booking-scroll-hint">
                <i className="fas fa-arrows-alt-h"></i> Deslice para ver más fechas
              </div>
            </div>

            <div className="booking-dates-container">
              {fechasVerificadas
                .filter((item) => item.fecha.getMonth() === mesServicio)
                .map((item, idx) => {
                  const esSel = fechaSeleccionada?.getTime() === item.fecha.getTime();
                  return (
                    <TarjetaFecha
                      key={idx}
                      item={item}
                      seleccionada={esSel}
                      onSelect={handleSeleccionarFecha}
                    />
                  );
                })}
            </div>
          </>
        )}
      </div>

      {fechaSeleccionada && (
        <div style={{ marginTop: '20px' }}>
          <h4 className="booking-time-title">
            <i className="fas fa-clock"></i>
            Horarios disponibles para{' '}
            {fechaSeleccionada.toLocaleDateString('es-ES', {
              weekday: 'long',
              day: 'numeric',
              month: 'long'
            })}
          </h4>

          {cargandoFechas && (
            <div className="booking-state-box loading">
              <i className="fas fa-spinner fa-spin" style={{ marginRight: '10px' }}></i>
              Cargando horarios disponibles...
            </div>
          )}

          {!cargandoFechas && (
            <div className="booking-time-grid">
              {horariosSeleccionados.map((horario, i) => (
                <TarjetaHorario
                  key={i}
                  horario={horario}
                  seleccionado={horaSeleccionada === horario.hora}
                  onSelect={handleSeleccionarHora}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectorFechas;
